import {
  Component,
  Input,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ChangeDetectorRef,
  EventEmitter,
  Output,
  ViewEncapsulation
} from '@angular/core';
import Moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { BcAuth } from 'common/auth/auth.service';
import { bcEnums } from 'common/common/constants/boldchat-enums';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { encode } from 'html-entities';
import { PersonType } from 'common/common/constants/person-type.enum';
import { NanorepService } from '../nanorep-panel/nanorep.service';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { AccessibilityFontSizeSetterService } from '../accessibility-font-size-setter/accessibility-font-size-setter.service';
import { RxEmitter } from '../../../common/rxjs/rx-emitter';
import { MessageBusEvents } from '../../../common/common/constants/message-bus-events';
import { Subscription } from 'rxjs';
import { AccessibilitySetting } from '../../../components/navbar/accessibility-settings/accessibility-setting.interface';
import { InnerTextPipe } from 'shared/components/canned-message-panel/filters/inner-text.pipe';
import { UserContextService } from 'common/user-context/user-context.service';
import { SmartAdvisorService } from 'shared/components/smart-advisor/smart-advisor.service';
import { BcFeatures } from 'common/common/constants/feature-enums';
import { BcSettingsService } from 'common/settings/settings.service';

@Component({
  selector: 'app-chat-messages',
  templateUrl: './chat-messages.component.html',
  styleUrls: ['./chat-messages.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ChatMessagesComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() workItemType;
  @Input() workItemId: string;
  @Input() messages;
  @Input() workItemCreated;
  @Input() isActive: boolean;
  @Input() canAgentProposeArticle: boolean;
  @Input() hasActiveRemoteControl: boolean;
  @Output() rendered: EventEmitter<void> = new EventEmitter();

  public types;
  public formattedWorkItemCreated;
  public translatedCopyText;
  public moment;
  public translate;
  public isArticleSuggestionEnabled;
  public isSmartAdvisor2Enabled: boolean;
  public isVideoFeatureEnabled: boolean;
  public isRemoteControlFeatureEnabled: boolean;
  public fontSizePercentage: number;
  public isFontSizeAccessibilitySettingTurnedOn: boolean = false;

  private _fontSizeAccessibilitySubscription: Subscription;

  constructor(
    private bcAuth: BcAuth,
    private _translateService: TranslateService,
    private _bcAnalytics: BcAnalytics,
    private _userContextService: UserContextService,
    private _accessibilityFontSizeSetterService: AccessibilityFontSizeSetterService,
    private _rxEmitter: RxEmitter,
    private _changeDetector: ChangeDetectorRef,
    private _nanorepService: NanorepService,
    private _innerTextPipe: InnerTextPipe,
    private _smartAdvisorService: SmartAdvisorService,
    private _bcSettingsService: BcSettingsService
  ) {
    this.moment = Moment;
    this.types = bcEnums.messageTypes;
  }

  async ngOnInit() {
    this.isFontSizeAccessibilitySettingTurnedOn =
      this._accessibilityFontSizeSetterService.isFontSizeAccessibilitySettingTurnedOn;

    this._fontSizeAccessibilitySubscription = this._rxEmitter.subscribe(
      MessageBusEvents.AccessibilitySetting + bcEnums.accessibilitySettingNames.CONVERSATION_FONT_SIZE,
      (fontSizeAccessibilitySetting: AccessibilitySetting) => {
        this._toggleFontSizeAccessibility(fontSizeAccessibilitySetting);
      }
    );
    this.formattedWorkItemCreated = this.moment(this.workItemCreated).format('llll');
    if ([WorkItemTypes.SMS, WorkItemTypes.FACEBOOK, WorkItemTypes.ASYNC_MESSAGE].includes(this.workItemType)) {
      this._translateService
        .get('bcChatMessages.copyTextChatId')
        .toPromise()
        .then((translated) => (this.translatedCopyText = translated));
    } else {
      this._translateService
        .get('bcChatMessages.copyChatId')
        .toPromise()
        .then((translated) => (this.translatedCopyText = translated));
    }
    this._collectFeatureValues();
    this.isSmartAdvisor2Enabled = await this._smartAdvisorService.getSmartAdvisorVersionValue();
  }

  ngAfterViewInit() {
    if (this.isFontSizeAccessibilitySettingTurnedOn) {
      this._setFontSize();
    }
    this._changeDetector.detectChanges();
    this.rendered.emit();
  }

  ngOnDestroy() {
    if (this._fontSizeAccessibilitySubscription) {
      this._fontSizeAccessibilitySubscription.unsubscribe();
      this._fontSizeAccessibilitySubscription = null;
    }
  }

  getLastMessageFormattedDate(messageItem) {
    const lastMessage = this.getLastMessage(messageItem);

    if (!lastMessage) {
      return '';
    }

    if (!lastMessage.formattedCreatedDate) {
      lastMessage.formattedCreatedDate = lastMessage.Created ? this.moment(lastMessage.Created).format('LTS') : '';
    }

    return lastMessage.formattedCreatedDate;
  }

  getLastMessageFormattedOperatorName(messageItem) {
    const lastMessage = this.getLastMessage(messageItem);
    const userId = this.bcAuth.getSessionUserId();

    if (!lastMessage) {
      return '';
    }
    return this.isActive && messageItem.personId === userId ? '' : ' - ' + lastMessage.Name;
  }

  getLastMessage(messageItem) {
    if (!messageItem.messages || !messageItem.messages.length) {
      return null;
    }

    return messageItem.messages[messageItem.messages.length - 1];
  }

  getMessageFormattedDate(message) {
    return this.moment(message.Created).format('LTS');
  }

  isChatWorkItemType() {
    return this.workItemType === WorkItemTypes.CHAT;
  }

  getType(msg) {
    if (msg.__typeCache) {
      return msg.__typeCache;
    } else {
      const type = ChatMessagesComponent.getMessageType(msg, this.workItemType);
      msg.__typeCache = type;
      return type;
    }
  }

  static getMessageType(msg, workItemType?) {
    let type;
    try {
      const json = JSON.parse(msg.JsonData);
      if (json.version) {
        type = json.listPicker
          ? 'listPicker'
          : json.payment
          ? 'payment'
          : json.timePicker
          ? 'timePicker'
          : 'interactive';
      }
    } catch (e) {
      type = msg.Type;
    }
    return workItemType === WorkItemTypes.CHAT
      ? 'chat'
      : !type
      ? 'link'
      : type.indexOf('image') === 0
      ? 'image'
      : type.indexOf('video') === 0
      ? 'video'
      : this.isInteractive(type)
      ? type
      : 'download';
  }

  isInteractiveType(type: string): boolean {
    return ChatMessagesComponent.isInteractive(type);
  }

  private static isInteractive(type: string): boolean {
    return ['listPicker', 'payment', 'timePicker', 'interactive'].indexOf(type) > -1;
  }

  getName(msg) {
    try {
      const json = JSON.parse(msg.Text);
      if (json && json.name) {
        return json.name;
      }
    } catch (e) {
      // Ignore
    }
    const url = this.getUrl(msg);
    const qi = url.indexOf('?');
    const path = url.substring(0, qi === -1 ? url.length : qi);
    const si = path.lastIndexOf('/');
    return si === -1 ? path : path.substring(si + 1);
  }

  getUrl(msg) {
    try {
      const json = JSON.parse(msg.Text);
      return (json && json.url) || msg.Text;
    } catch (e) {
      return msg.Text;
    }
  }

  getInteractiveText(msg) {
    return ChatMessagesComponent.getInteractiveText(msg.Text, msg.JsonData, msg);
  }

  sanitizeMessage(msg): string {
    return encode(msg);
  }

  static getInteractiveText(text, jsonData, msg?) {
    try {
      const json = JSON.parse(jsonData);
      const parts = [];
      Object.keys(ChatMessagesComponent.interactiveProcessors).forEach((key) => {
        const processor = ChatMessagesComponent.interactiveProcessors[key];
        const obj = ChatMessagesComponent.evaluateObject(json, key);
        if (obj) {
          processor(parts, obj, msg, json);
        }
      });
      return parts.join('');
    } catch (e) {
      return text;
    }
  }

  static evaluateObject(obj, name) {
    if (!obj) {
      return null;
    }
    if (!name) {
      return obj;
    }
    const di = name.indexOf('.');
    if (di === -1) {
      return obj[name];
    } else {
      return ChatMessagesComponent.evaluateObject(obj[name.substring(0, di)], name.substring(di + 1));
    }
  }

  // code smell: why "msg" parameter is not used?
  static getInteractiveImage(data, _msg) {
    let found = '';
    if (data.image && data.image.url) {
      found = data.image.url;
    } else if (data.image && data.image.data) {
      found = 'data:image;base64,' + data.image.data;
    }
    if (found === '') {
      return data.title;
    }

    return `<span style="height: 36px; display: flex; align-items: center; justify-content: center"><img style="display: inline; height: 32px; margin: 2px" align="middle" src="${found}" /> ${data.title}</span>`;
  }

  static interactiveProcessors: { [key: string]: (parts: any[], obj: any, msg?: any, json?: any) => void } = {
    '': (parts, receivedMessage, msg, json) => {
      parts.push('<div>');
      parts.push(ChatMessagesComponent.getInteractiveImage(receivedMessage, json));
      parts.push('</div>');
    },
    addresses: (parts, addresses) => {
      parts.push('<ul>');
      addresses.forEach((address) => {
        parts.push('<li>');
        parts.push(address.title);
        parts.push('</li>');
      });
      parts.push('</ul>');
    },
    'timePicker.timeslots': (parts, timeslots) => {
      parts.push('<ul>');
      timeslots.forEach((timeslot) => {
        const start = new Date(timeslot.startTime);
        parts.push('<li>');
        parts.push(start.toLocaleString());
        parts.push('</li>');
      });
      parts.push('</ul>');
    },
    'listPicker.sections': (parts, sections, msg, json) => {
      sections.forEach((section, i) => {
        if (i > 0 && msg && msg.PersonType === PersonType.Visitor) {
          return;
        }

        if (section.items) {
          parts.push('<ul>');
          section.items.forEach((item) => {
            parts.push('<li>');
            parts.push(ChatMessagesComponent.getInteractiveImage(item, json));
            parts.push('</li>');
          });
          parts.push('</ul>');
        }
      });
    },
    'payment.lineItems': (parts, lineItems) => {
      parts.push('<ul>');
      lineItems.forEach((line) => {
        parts.push('<li>');
        parts.push(line.title + ': ' + line.amount);
        parts.push('</li>');
      });
      parts.push('</ul>');
    },
    'payment.paymentMethod': (parts, paymentMethod) => {
      parts.push('<div>');
      parts.push('Payment method: ' + paymentMethod);
      parts.push('</div>');
    },
    URL: (parts, url, msg?) => {
      parts.push(ChatMessagesComponent.getInteractiveText('', decodeURIComponent(url), msg));
    }
  };

  private async _collectFeatureValues() {
    this.isArticleSuggestionEnabled = this.canAgentProposeArticle && (await this._nanorepService.hasNanorepFeature());
    this.isVideoFeatureEnabled = await this._bcSettingsService
      .getSettings()
      .then(
        (settings) =>
          this._bcSettingsService.ChatFeatures.isEnabled(settings, BcFeatures.LC_VIDEO_CHAT) &&
          this._bcSettingsService.ChatFeatures.isEnabled(settings, BcFeatures.LC_NEW_VIDEO_CHAT)
      );
    this.isRemoteControlFeatureEnabled = await this._bcSettingsService
      .getSettings()
      .then((settings) => this._bcSettingsService.ChatFeatures.isEnabled(settings, BcFeatures.RC_NEW_REMOTE_CONTROL));
  }

  toggleSuggestionButtonVisibility(msg) {
    msg.clickedSuggestion = true;
  }

  async proposeArticle(msg, index): Promise<void> {
    delete msg.clickedSuggestion;
    msg.propose = 'bcChatMessages.articleSuggestionInProgress';
    this._bcAnalytics.eventTrack('NanorepSuggestArticle');
    const lastVisitorMsg = this._getLastVisitorMsgBeforeSuggestedArticle(msg, index);
    const lastVisitorMsgText = this._innerTextPipe.transform(lastVisitorMsg).trim();
    const lastAgentMsgText = this._innerTextPipe.transform(msg.Text).trim();
    const isSmartAdvisor2Enabled = await this._smartAdvisorService.getSmartAdvisorVersionValue();

    const sendContent = (content, title) => {
      return this._nanorepService.sendMessageToProposeAnArticle(msg.ChatID, content, title);
    };

    if (isSmartAdvisor2Enabled) {
      return this._smartAdvisorService
        .suggestArticle({
          agentMessage: lastAgentMsgText,
          visitorMessage: lastVisitorMsgText
        })
        .then(({ content, title }) => {
          this._smartAdvisorService.showSuggestionSentMessage();
          return sendContent(content, title);
        })
        .catch(() => {
          this._smartAdvisorService.showSuggestionSentMessage();
        });
    } else {
      return sendContent(msg.Text, lastVisitorMsgText)
        .then(() => {
          msg.propose = 'bcChatMessages.articleWasSent';
        })
        .catch(() => {
          msg.propose = 'bcChatMessages.articleSuggestionError';
        });
    }
  }

  isOwnMessage(personId: string): boolean {
    return personId === this._userContextService.userContext.id;
  }

  private _getLastVisitorMsgBeforeSuggestedArticle(msg, msgGroupIndex): string {
    for (let i = msgGroupIndex - 1; i >= 0; i--) {
      if (this.messages[i].type === this.types.VISITOR) {
        const lastBlockOfVisitorMessages = this.messages[i].messages;
        const lastVisitorMessageIndex = lastBlockOfVisitorMessages.length - 1;
        const lastVisitorMessage = lastBlockOfVisitorMessages[lastVisitorMessageIndex].Text;
        return lastVisitorMessage;
      }
    }
    return '';
  }

  public onFontSizePercentageChanged(newFontSizePercentage): void {
    this.fontSizePercentage = newFontSizePercentage;
  }

  public onDefaultFontSizePercentageSet(): void {
    this._setFontSize();
  }

  public getAccessibilityClass(): string {
    if (this.isFontSizeAccessibilitySettingTurnedOn && this.fontSizePercentage) {
      return 'zoom-' + this.fontSizePercentage;
    }
    return;
  }

  private _setFontSize(): void {
    this.fontSizePercentage = this._accessibilityFontSizeSetterService.fontSizePercentage;
  }

  private _toggleFontSizeAccessibility(setting: AccessibilitySetting): void {
    this.isFontSizeAccessibilitySettingTurnedOn = setting.isTurnedOn;
  }
}
